import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BestSellersSection = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('buy2get70');
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef(null);

  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?limit=6`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching best sellers:', error);
      }
    };
    fetchProducts();
  }, []);

  const tabs = [
    { id: 'buy2get70', label: 'Buy 2 get 70% Off' },
    { id: 'bestsellers', label: 'Best Sellers' },
    { id: 'neverstyle', label: 'Never Out of Style' }
  ];

  // Handle mouse enter on section
  const handleMouseEnter = () => {
    checkScroll();
  };

  // Handle mouse leave on section
  const handleMouseLeave = () => {
    setShowLeftArrow(false);
    setShowRightArrow(false);
  };

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  // Scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  return (
    <section 
      className="py-2 bg-white relative" 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-screen-2xl mx-auto">
        {/* Header with Tabs and Shop Now */}
        <div className="px-4 md:px-16 lg:px-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-3 md:gap-4">
          {/* Tabs Navigation - Responsive */}
          <div className="flex md:border md:border-gray-300 w-full md:w-auto overflow-x-auto scrollbar-hide">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 md:px-6 md:py-3 font-semibold text-xs md:text-sm transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                } ${index !== 0 ? 'md:border-l md:border-gray-300' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Shop Now Button - Right aligned */}
          <Link 
            to="/products"
            className="text-black font-bold text-sm underline hover:no-underline transition-all whitespace-nowrap self-start md:self-auto"
          >
            Shop Now
          </Link>
        </div>
        
        {/* Horizontal Line */}
        <div className="border-t-2 border-gray-400 my-6"></div>
        </div>

        {/* Products Grid with left and right padding and scroll */}
        <div className="relative">
          {/* Left Arrow - Hidden on mobile */}
          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border-2 border-black w-12 h-12 items-center justify-center hover:bg-gray-100 transition-all"
              style={{ marginLeft: '-24px' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Right Arrow - Hidden on mobile */}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border-2 border-black w-12 h-12 items-center justify-center hover:bg-gray-100 transition-all"
              style={{ marginRight: '-24px' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Scrollable Products Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className="overflow-x-auto scrollbar-hide scroll-smooth px-4 md:pl-16 lg:pl-20"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-4 md:gap-3" style={{ minWidth: 'max-content' }}>
          {products.map((product) => (
            <div 
              key={product._id}
              className="group bg-white hover:shadow-xl transition-all duration-300 cursor-pointer relative flex-shrink-0 w-[calc(100vw-2rem)] md:w-[280px] flex flex-col"
              onClick={() => navigate(`/product/${product._id}`)}
            >
              {/* Product Image */}
              <div className="relative overflow-hidden bg-gray-100 mb-3">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-[400px] md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                
                {/* Wishlist Heart Icon */}
                <button className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-md">
                  <svg 
                    className="w-6 h-6 text-gray-700" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                    />
                  </svg>
                </button>
              </div>

              {/* Product Info */}
              <div className="px-3 flex-1 flex flex-col">
                {/* Price Section */}
                <div className="mb-2">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xl font-bold text-red-600">
                      ₹{product.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    {product.originalPrice && (
                      <span className="text-gray-500 line-through">
                        ₹{product.originalPrice.toFixed(2)}
                      </span>
                    )}
                    {product.discount && (
                      <span className="text-red-600 font-semibold">
                        -{product.discount}%
                      </span>
                    )}
                  </div>
                </div>

                {/* Product Name */}
                <h3 className="font-bold text-base text-gray-900 mb-1 line-clamp-2">
                  {product.name}
                </h3>

                {/* Category */}
                <p className="text-sm text-gray-600 mb-3">
                  {product.category}
                </p>

                {/* Add to Cart - Always visible, full width, pushed to bottom */}
                <button className="w-full bg-black text-white py-3 rounded font-semibold text-sm transition-all duration-300 hover:bg-gray-800 mt-auto">
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BestSellersSection;
